import React from 'react'
import { cn } from '@/lib/utils'
import { formatRateSym } from '@/lib/calendar-utils'
import { useAnimatedNumber } from '@/lib/useAnimatedNumber'

interface AnimatedRateProps {
  /** The backend-resolved amount. Null renders the placeholder. */
  value: number | null
  sym: string
  className?: string
  /** Dim while a newer preview is in flight — reads as "settling", not "wrong". */
  stale?: boolean
  placeholder?: string
}

/**
 * A money figure that eases between backend-resolved values instead of snapping.
 *
 * Every value shown here comes from the server; the tween is presentation. The
 * displayed string is never read back into state or saved — see
 * useAnimatedNumber for why that keeps the no-frontend-math rule intact.
 */
export default function AnimatedRate({
  value,
  sym,
  className,
  stale = false,
  placeholder = '—',
}: AnimatedRateProps) {
  const animated = useAnimatedNumber(value)

  return (
    <span
      className={cn('tabular-nums transition-opacity duration-200', stale && 'opacity-50', className)}
      // The exact target for assistive tech and tests — never the eased frame.
      aria-label={value != null ? formatRateSym(value, sym) : undefined}
    >
      {animated != null ? formatRateSym(Math.round(animated * 100) / 100, sym) : placeholder}
    </span>
  )
}
